import {
    BackOfficeDeliveryIcon,
    BackOfficeDeliveryMessage,
    BackOfficeSyncIcon,
    Geometry,
} from 'types';
import { FormatTypeToInputType } from './helpers';
import { Ticket } from './User/firebaseTypes/ticket';

export type DeviceLastReading = {
    time: number;
    /** Estimated tank level percentage (between 0 and 100). */
    tank: number;
    battery: number | null;
    temperature: number | null;
    voltage: number | null;
    insert_time: number | null;
    /** Battery warning (need to replace batteries soon). */
    battery_warn: number | null;
    /** Battery critical (need to replace batteries immediately to avoid going offline). */
    battery_crit: number | null;
    rssi: number;
    chn: number;
    /** Signal Power in units dBm. */
    rsrp: number | null;
    /** Signal Quality in units dB. */
    rsrq: number | null;
    band: number;
    /** The amount, in gallons, by which the tank falls short of being full. */
    ullage?: number;
    device_id?: string;
    insert_time_iso?: string;
    supplier_id?: string;
    gallons?: number;
};

/** Fuel consumption types mapped to boolean values indicating whether a device is used for that consumption purpose or not. */
export type DeviceConsumptionType = {
    bulk_storage: boolean;
    commercial_industrial: boolean;
    cooking: boolean;
    fireplace: boolean;
    generator: boolean;
    heating: boolean;
    hot_water: boolean;
    laundry_dryer: boolean;
    pool: boolean;
    retail_fill_up: boolean;
    backup_heating: boolean;
};

export type DeviceHologramUsage = {
    ota_conn_count: number;
    bytes: number;
    conn_count: number;
    ota_byes: null;
};

export type DeviceLastPrediction = {
    /** Number of Heating Degree Days (HDD). */
    hdd: number;
    predictions: DevicePrediction[];
    /** A number representing predicted gallons to be consumed that day. */
    consumption?: number;
    datetime?: string;
    /** A formatted number/string of a date in YYYYMMDD format like "20221030" where that represents October 30th, 2022.*/
    dd?: string;
    /**  Estimated tank level percentage (between 0 and 100). */
    tank?: number;
};

export type DevicePrediction = {
    /** A number representing predicted gallons to be consumed that day. */
    consumption: string | number;
    /** A string of a number. */
    consumptionCAvg7: string;
    datetime: Date | string;
    /** A formatted number/string of a date in YYYYMMDD format like "20221030" where that represents October 30th, 2022.*/
    dd: number | string;
    /** Number of Heating Degree Days (HDD). */
    hdd: number;
    /**  Estimated tank level percentage (between 0 and 100). */
    tank: string | number;
    /** A string of a number. */
    tankCAvg7: string;
};

export type DeviceOrientationType = 'horizontal' | 'vertical';
export type DeviceFuelType = '' | 'propane' | 'oil';
export type DeviceConnectionType = 'wifi' | 'cell' | 'virtual';
export type DeviceGaugeType = 'spiral' | 'swing_arm';

export type DeviceFillupType = {
    amount?: number;
    time?: Date;
};

export type DeviceBillingConfig = {
    /** Tax rate to charge for this device. taxPercentage = 6.25 would mean 6.25%. */
    taxPercentage?: number | null;
    pricePerGallon?: number | null;
};

export type Device = {
    /** The name of the device/tank. */
    name: string;
    address: string;
    geometry?: Geometry;
    /** The long id. */
    deviceId: string;
    shortDeviceId: string;
    /** accountId is a field associated with third-party backoffice software. It encodes accountNumber and tankNumber data like so: `accountId = ${accountNumber}-${tankNumber}`). */
    accountId: string;
    fuelType: DeviceFuelType;
    /** The capacity of the tank in unit gallons. */
    capacity: number;
    connection_type: DeviceConnectionType;
    /** E.g., 'deployed', 'sleep', 'shipped', 'retired' etc. */
    status: string;
    consumptionType?: DeviceConsumptionType | null;
    orientation: DeviceOrientationType;
    customer_name: string;
    /** E.g., 'lte', 'photon', 'virtual_platform' etc. */
    platform: string;
    /** E.g., 'hologram', 'floLive', etc. */
    carrier: string;
    product_name: string;
    product_id: string;
    /** The amount, in gallons, by which the tank falls short of being full. */
    ullage: number;
    /* Change of Tank Level Since Last Reading (%) */
    /** The measured change in the tank level % that will trigger an additional reading. */
    // Example for COV format: If you want to set cov to 20% of tank capacity, you would store cov to 20.
    cov?: number;
    /** The monitor will send a reading by this time. Only one reading will be sent every 24 hours. */
    ftt?: number;
    /** True if the device is a virtual device. */
    virtual: boolean;
    /** User-provided notes for delivery instructions. */
    delivery_instructions?: string;
    /** dialType can be set to override the equation id used for Gen5+ units */
    dialType?: string;
    /** User-provided notes about the device. */
    notes?: string;
    lat: number;
    lng: number;
    /** Hardware revision. */
    hw_rev: string;
    /** Software revision. */
    sw_rev: string;
    deployed_time: number;
    lastReading: DeviceLastReading | null;
    /** lastActiveDelivery: Shows either the currently active delivery ticket, or a resolved delivery ticket for the most recent delivery, or empty */
    lastActiveDelivery: Ticket | null;
    gaugeType?: string;
    mfg_tla: string;
    /** Battery critical level (need to replace batteries immediately to avoid going offline). */
    battery_crit: number;
    /** Battery warning level (need to replace batteries soon). */
    battery_warn: number;
    item_number: string;
    l30: string;
    /** Last 15 voltage. */
    v_15: string;
    /** Last 15 temp. */
    t_15: string;
    c_avg_7: number;
    c_avg_30: number;
    p_avg_7: number;
    p_avg_30: number;
    fillups?: DeviceFillupType[];
    /** days until fillup, used for estimating when device will next need a fillup */
    duf_7: number;
    /** elasticsearch update time */
    es_update_time: number;
    estimated_tank?: number;
    virtual_supplier: string[];
    p_avg_3: number;
    /** Gallons is the current amount of fuel in the tank in unit gallons. */
    gallons: number;
    c_avg_3: number;
    /**
     *  @see SupplierGroup
     * The name of the group the device belongs to.
     */
    group?: string | null;
    mfg_tla_rev: string;
    hologram_usage: DeviceHologramUsage;
    /** Transmission/Reporting interval. The number of hours between readings being updated in the portal. */
    t_int: number;
    mfg_tla_dev: string;
    clone_time: null;
    /** Reading interval. The number of hours between comparison to the Change of Tank Level threshold measurements. */
    r_int: number;
    x30: string;
    imei: string;
    hardware_review: null;
    /** The name of the delivery type. */
    delivery_type?: string | null;
    rsrp_avg: number;
    lastPrediction: DeviceLastPrediction;
    std_dev: number;
    /** Warning Threshold (%) If tank level drops below this % it will trigger an additional reading. */
    thresh1: number;
    /** Critical Threshold (%) If tank level drops below this % it will trigger an additional reading. */
    thresh2: number;
    /** Change of Tank Level Since Last Transmission (%) */
    // Example for tcov format: If you want to set tcov to 20% of tank capacity, you would store tcov to 20.
    tcov: number;
    status_history: {
        /** Timestamp like 1662490004450 */
        deployed_time: number;
        /** "2022-09-06T18:46:44Z" */
        deployed_time_iso: Date | string;
    };
    equation_id?: string;
    refresh_reading_data?: boolean;
    sensor_type?: string;
    signal?: string;

    /** Timestamp like "2022-10-30T00:00:00Z" */
    lastReadingTime: Date | string;
    billing?: DeviceBillingConfig;
    /** If this device is a clone, this value is the deviceId of the original device. */
    clone_of?: string;

    /*
        Backoffice Fields
    */
    /** Whether or not backoffice auto sync is configured for the device. If it is, many device info fields are NOT editable by the user. */
    bo_sync_config?: boolean;
    bo_delivery_time: null;
    bo_sync_msg: string; // e.g., "ftp_sync"
    /** Timestamp. */
    bo_sync_time?: number;
    bo_has_open_delivery: null;
    _bo_delivery_msg?: BackOfficeDeliveryMessage | null;
    /** CSS style string to format _delivery_icon. Used in ops-portal. */
    _delivery_style?: string | null;
    _delivery_icon?: BackOfficeDeliveryIcon | null;
    _has_delivery?: boolean | null;
    _bo_sync_complete?: boolean | null;
    _backoffice_icon?: BackOfficeSyncIcon | null;
    /** CSS style string to format backoffice_icon. Used in ops-portal. */
    _backoffice_style?: string | null;

    /*
       Below Fields Added During Client-side Processing
    */

    health?: DeviceHealth;
    /** Is the device currently connected or not. */
    isConnected?: boolean;
    /** Timestamp of order date, matches the shipment `time` field. */
    orderDate?: number;

    protected?: DeviceProtected;
};

export interface DeviceProtected {
    supplierId?: string;
}

export interface DeviceStatusSummary {
    /** The device status (e.g., deployed, shipped, sleep, etc.) */
    status: string;
    /** Number of devices that have the associated status. */
    count: number;
    /** Whether or not this field something we display to customers. */
    isCustomerFacing: boolean;
}

export interface DeviceHealth {
    icon: string; // From https://mui.com/material-ui/material-icons/
    color: string;
    tooltip: string;
}

export type SharedDeviceInput = FormatTypeToInputType<
    Pick<
        Device,
        | 'fuelType'
        | 'gaugeType'
        | 'capacity'
        | 'orientation'
        | 'delivery_instructions'
        | 'address'
        | 'customer_name'
        | 'name'
        | 'group'
    > & {
        consumptionType: (keyof DeviceConsumptionType)[];
    }
>;

type DeviceInstallAttempts = {
    gaugePicture: { url: string; path: string };
    tankPicture: { url: string; path: string };
};

export type PhysicalDeviceInput = SharedDeviceInput & {
    // the following fields are temporary fields that should be mapped on submit
    deviceId: string;
    estimatedTankLevelPct: number;
    lastReading: Partial<DeviceLastReading>;
    tankEstimateCorrect: boolean;
    installAttempts: DeviceInstallAttempts;
    shouldRetireDevice: boolean | null;
};

export type VirtualDeviceInput = SharedDeviceInput &
    FormatTypeToInputType<Pick<Device, 'fillups'>>;

export type MappedVirtualDeviceInput = Omit<
    VirtualDeviceInput,
    'consumptionType' | 'fillups'
> & {
    consumptionType: DeviceConsumptionType;
    /** True if the device is a virtual device. */
    virtual: boolean;
    connection_type: string;
    protected: { supplierId: string };
};

export type MappedPhysicalDeviceInput = Omit<
    PhysicalDeviceInput,
    'consumptionType'
> & {
    deviceId: string;
    consumptionType: DeviceConsumptionType;
    protected: { supplierId: string };
};

export type VirtualDeviceInputFillup = {
    /** The capacity of the tank in unit gallons. */
    capacity: number;
    partial: string;
    diff: number | null;
    after: number;
    before: number | null;
    amount: number;
    time: Date;
};

export const DEVICE_GAUGE_PICTURE_ID = 0;
export const DEVICE_TANK_PICTURE_ID = 1;

/** 0 is gauge picture, 1 is a site picture */
export type DevicePictureTypeId = 0 | 1;
/** An object containing only a time as a string. */
export type TimestampObject = {
    time: string;
};

/** A battery history entry returned in a detailed device health object. */
export type BatteryHistoryEntry = TimestampObject & {
    /** Battery critical (need to replace batteries immediately to avoid going offline). */
    battery_crit: number;
    /** Battery warning (need to replace batteries soon). */
    battery_warn: number;
};

/** A signal history entry returned in a detailed device health object. */
export type SignalHistoryEntry = TimestampObject & {
    /** Signal Power in units dBm. */
    rsrp: number;
    /** Signal Quality in units dB. */
    rsrq: number;
};

export type TelemetryHistoryEntry = TimestampObject & {
    errors?: number[];
};

/** The type returned by the device/DEVICE_ID/health endpoint. */
export type DeviceDetailedHealth = {
    additional: string;
    batteryHist: BatteryHistoryEntry[];
    /** Health flag. Low battery. */
    battery_err: boolean;
    battery_status: string;
    dev_type: string;
    device_id: string;
    /** Health flag. Escalate. */
    escalate: boolean;
    /** Health flag. Network banned. */
    fplmn_banned: boolean;
    ftt_crit: boolean;
    fw_rev: string;
    /** Health Rating. */
    healthy: string;
    hf_prop: number;
    last_reading: string;
    last_reading_time: number;
    minVoltage: number;
    /** Health flag. Move to new location. */
    new_loc: boolean;
    /** Offline Rate. */
    off_prop: number;
    /** Health flag. Offline. */
    offline: boolean;
    /** Health flag. Offline (dead). */
    offline_dead: boolean;
    /** Health flag. Offline (recent)). */
    offline_recent: boolean;
    /** Success Rate. */
    rate: number;
    /** RSRP value in English. E.g., Excellent, Good, Fair, Poor, etc. */
    rsrp_eng: string;
    /** RSRQ value in English. E.g., Horrendous, Good, Fair to Poor, Excellent, etc. */
    rsrq_eng: string;
    rssi: string[];
    sensor_voltage: number[];
    signalHist: SignalHistoryEntry[];
    telemetry_errors?: TelemetryHistoryEntry[];
    temps: number[];
    time: number[];
    voltage: number[];
    /** Signal Power in units dBm. */
    rsrp?: number[];
    /** Signal Quality in units dB. */
    rsrq?: number[];
};

export type DeviceReading = {
    battery: number;
    /** Battery critical (need to replace batteries immediately to avoid going offline). */
    battery_crit: number;
    /** Battery warning (need to replace batteries soon). */
    battery_warn: number;
    /** The measured change in the tank level % that will trigger an additional reading. */
    cov: number;
    device_id: string;
    /** Error interval. */
    e_int: number;
    equation_id: string;
    /** The monitor will send a reading by this time. Only one reading will be sent every 24 hours. */
    ftt: number;
    /** Gallons is the current amount of fuel in the tank in unit gallons. */
    gallons: number;
    hide: number;
    insert_time: number;
    insert_time_iso: string;
    mdm_rev: number;
    old_time: Date | number | string;
    /** Reading interval. The number of hours between comparison to the Change of Tank Level threshold measurements. */
    r_int: number;
    sensor_voltage: number;
    sig: number;
    supplier_id: string;
    /** Software revision. */
    sw_rev: string | number;
    /** Transmission/Reporting interval. The number of hours between readings being updated in the portal. */
    t_int: number;
    /** Estimated tank level percentage (between 0 and 100). */
    tank: number;
    tank_act: number;
    temperature: number;
    /** Warning Threshold (%) If tank level drops below this % it will trigger an additional reading. */
    thresh1: number;
    /** Critical Threshold (%) If tank level drops below this % it will trigger an additional reading. */
    thresh2: number;
    time: Date | string | number;
    time_iso: string;
    /** The amount, in gallons, by which the tank falls short of being full. */
    ullage: number;
    voltage: number;
    _id: string;
};

export type DeviceDeploymentData = {
    address: string;
    cloneInfo?: boolean;
    date: Date | number | string;
    duration: number;
    index: string;
    install: boolean;
    result: boolean;
    id: string;
};

export type DeviceAudit = {
    id: string;
    email: string;
    new_val: any;
    old_val: any;
    supplierId: string;
    time: Date | number | string;
    time_iso: string;
    uid?: string;
};

export type DeviceConsumptionPredictionsTable = {
    [date: string]: DevicePrediction;
};

export type ProcessedPrediction = {
    date: number;
    /** A number representing predicted gallons to be consumed that day. */
    consumption: number | null;
    /** Number of Heating Degree Days (HDD). */
    hdd: number | null;
    /** Estimated tank level percentage (between 0 and 100). */
    tank: number | null;
};

export type DeviceConsumptionResultEntry = {
    /** Number of Heating Degree Days (HDD). */
    hdd: number;
    avg?: number;
    count?: number;
    diffavg?: number;
    diffcount?: number;
    diffmax?: number;
    diffmin?: number;
    diffsum?: number;
    max?: number;
    min?: number;
    sum?: number;
    temp?: number;
    temp_avg?: number;
    temp_max?: number;
    temp_min?: number;
    temp_sum?: number;
    /** A number representing predicted gallons to be consumed that day. */
    consumption?: number;
    predictions?: DeviceConsumptionPredictionsTable;
};

export type DeviceConsumptionResult = {
    [date: string]: DeviceConsumptionResultEntry;
};

export type DeviceReadingsTelemetryEntry = {
    attempt_no: number;
    band: number;
    cell_id: string;
    chn: number;
    fplmn: number[];
    http_status_code: number;
    module_temp: number;
    module_voltage: number;
    plmn: number;
    rat: string;
    /** Signal Power in units dBm. */
    rsrp: number;
    /** Signal Quality in units dB. */
    rsrq: number;
    rssi: number;
    srxlev: number;
    state: string;
    time_to_conn: number;
    tlm_time: number;
    type: string;
};

export type DeviceReadingsResultEntry = {
    apt: number;
    attc: number;
    band: number;
    battery: number;
    /** Battery critical (need to replace batteries immediately to avoid going offline). */
    battery_crit: number;
    /** Battery warning (need to replace batteries soon). */
    battery_warn: number;
    cf: number;
    chn: number;
    /** The measured change in the tank level % that will trigger an additional reading. */
    cov: number;
    device_id: string;
    /** Error interval. */
    e_int: number;
    equation_id: string;
    /** The monitor will send a reading by this time. Only one reading will be sent every 24 hours. */
    ftt: number;
    /** Gallons is the current amount of fuel in the tank in unit gallons. */
    gallons: number;
    imei: string;
    insert_time: number;
    insert_time_iso: string;
    last_err_code: number;
    last_err_msg: string;
    mcf: number;
    mdm_rev: string;
    /** Reading interval. The number of hours between comparison to the Change of Tank Level threshold measurements. */
    r_int: number;
    /** Signal Power in units dBm. */
    rsrp: number;
    /** Signal Quality in units dB. */
    rsrq: number;
    rssi: number;
    scf: number;
    sensor_voltage: number;
    sig: number;
    sim_id: string;
    supplier_id: string;
    /** Software revision. */
    sw_rev: string;
    /** Transmission/Reporting interval. The number of hours between readings being updated in the portal. */
    t_int: number;
    /** Estimated tank level percentage (between 0 and 100). */
    tank: number;
    tank_act: number;
    /** Change of Tank Level Since Last Transmission (%) */
    tcov: number;
    telemetry: DeviceReadingsTelemetryEntry[];
    temperature: number;
    /** Warning Threshold (%). If tank level drops below this % it will trigger an additional reading. */
    thresh1: number;
    /** Critical Threshold (%). If tank level drops below this % it will trigger an additional reading. */
    thresh2: number;
    time: number;
    time_iso: string;
    /** The amount, in gallons, by which the tank falls short of being full. */
    ullage: number;
    voltage: number;
    _id: string;
    hide?: boolean;
};

export type DeviceReadingsResult = {
    data: DeviceReadingsResultEntry[];
    count: number;
};

export type DeviceReadingsDictionaryResult = {
    [uuid: string]: DeviceReadingsResultEntry;
};
